import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { openMatchStream } from "../lib/ws";
import {
  searchFood, matchCurrent, matchCreate, matchJoin, matchBegin, matchVote, matchLeave, matchClose,
} from "../lib/api";

const BASE = process.env.REACT_APP_API_BASE ?? "/api";

// pfp endpoint is auth'd; the token must ride in the query string for <img> loads.
function buildPfpSrc(url, token) {
  if (!url) return null;
  const full = url.startsWith("http") ? url : `${BASE}${url.startsWith("/") ? "" : "/"}${url}`;
  const join = full.includes("?") ? "&" : "?";
  return `${full}${join}t=${encodeURIComponent(token)}`;
}

const CAT_LABEL = {
  restaurant: "Restaurante", fast_food: "Comida rápida", cafe: "Cafetería", bar: "Bar",
  pub: "Pub", food_court: "Food court", ice_cream: "Heladería", biergarten: "Cervecería",
  bakery: "Panadería", deli: "Delicatessen", confectionery: "Confitería", pastry: "Pastelería",
};
const catLabel = (c) => CAT_LABEL[c] || (c ? c.replace(/_/g, " ") : "Sitio");
const prettyCuisine = (c) => (c ? c.split(";")[0].replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : null);
const fmtDist = (m) => (m == null ? "" : m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`);
const placeIdOf = (p) => `${p.osmType}/${p.osmId}`;

const LIGHT_TILES = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const FEATURE_LABEL = {
  outdoor_seating: "Terraza", indoor_seating: "Sala interior", takeaway: "Para llevar",
  delivery: "A domicilio", vegetarian: "Vegetariano", vegan: "Vegano",
  wheelchair: "Accesible", reservation: "Reserva",
};
const socialUrl = (base, v) => (!v ? null : /^https?:\/\//i.test(v) ? v : base + v.replace(/^@/, ""));
const pinIcon = L.divIcon({
  className: "gm-pin-wrap", html: `<span class="gm-pin-dot"></span>`,
  iconSize: [24, 24], iconAnchor: [12, 12],
});
function MiniPan({ lat, lon }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lon], map.getZoom(), { animate: false }); }, [lat, lon, map]);
  return null;
}

function tallyMapFromList(list) {
  const out = {};
  (list || []).forEach((t) => { out[t.placeId] = { yes: t.yes, no: t.no, dontCare: t.dontCare }; });
  return out;
}

export default function GroupMatch({ center, radius, osmTypes, onExit }) {
  const [snap, setSnap] = useState(null);
  const [tally, setTally] = useState({});
  const [gIdx, setGIdx] = useState(0);
  const [view, setView] = useState("votar");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const token = localStorage.getItem("fw_token") || "";
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  // Initial snapshot.
  useEffect(() => {
    let alive = true;
    matchCurrent().then((s) => { if (alive) setSnap(s); }).catch(() => { if (alive) setSnap({ exists: false }); });
    return () => { alive = false; };
  }, []);

  const joined = !!(snap && snap.exists && snap.youJoined);
  const sessionId = snap && snap.sessionId;

  // Live stream — opens once we're a participant. Server resends a full init on
  // every (re)connect, so a brief drop can never lose the "locked" transition.
  useEffect(() => {
    if (!joined || !sessionId) return undefined;
    const stop = openMatchStream(
      BASE, token,
      (state) => { setSnap(state); setTally(tallyMapFromList(state.tally)); },
      (name, data) => {
        if (name === "participant_joined" || name === "participant_left") {
          setSnap((prev) => (prev ? { ...prev, participants: (data && data.participants) || [] } : prev));
        } else if (name === "locked") {
          setSnap((prev) => (prev ? {
            ...prev, phase: "locked", deck: (data && data.deck) || [],
            lat: data && data.lat, lon: data && data.lon, radius: data && data.radius, types: data && data.types,
          } : prev));
          setTally({});
          setGIdx(0);
        } else if (name === "vote" && data && data.placeId) {
          setTally((prev) => ({ ...prev, [data.placeId]: { yes: data.yes, no: data.no, dontCare: data.dontCare } }));
        } else if (name === "finished") {
          setSnap((prev) => (prev ? { ...prev, phase: "finished" } : prev));
          if (data && data.tally) setTally(tallyMapFromList(data.tally));
          setView("resultados");
        } else if (name === "closed") {
          setNotice("La sesión ha terminado.");
          setTimeout(() => onExitRef.current && onExitRef.current(), 900);
        }
      }
    );
    return stop;
  }, [joined, sessionId, token]);

  const doCreate = useCallback(async () => {
    setBusy(true);
    try { setSnap(await matchCreate()); } catch (e) {
      setNotice(e.message === "409" ? "Ya hay una sesión abierta." : "No se pudo crear.");
      try { setSnap(await matchCurrent()); } catch {}
    } finally { setBusy(false); }
  }, []);

  const doJoin = useCallback(async () => {
    setBusy(true);
    try { setSnap(await matchJoin()); } catch (e) {
      setNotice(e.message === "409" ? "La sesión ya está bloqueada." : "No se pudo unir.");
      try { setSnap(await matchCurrent()); } catch {}
    } finally { setBusy(false); }
  }, []);

  const doBegin = useCallback(async () => {
    setBusy(true);
    try {
      // Fetch the deck via the proven /food/search path, then hand it to begin so
      // the server never makes a second (possibly slow) Overpass call under lock.
      let deck = [];
      try { deck = await searchFood({ lat: center[0], lon: center[1], radius, types: osmTypes }); } catch {}
      const s = await matchBegin({ lat: center[0], lon: center[1], radius, types: osmTypes, deck });
      setSnap(s); setTally(tallyMapFromList(s.tally)); setGIdx(0); setView("votar");
    } catch (e) {
      setNotice(`No se pudo empezar (${e.message || "error"}).`);
    } finally { setBusy(false); }
  }, [center, radius, osmTypes]);

  const deck = useMemo(() => (snap && snap.deck) || [], [snap]);
  const deckById = useMemo(() => {
    const m = {};
    deck.forEach((p) => { m[placeIdOf(p)] = p; });
    return m;
  }, [deck]);

  const doVote = useCallback(async (value) => {
    const p = deck[gIdx];
    if (!p) return;
    setGIdx((i) => i + 1); // advance immediately; tally arrives via broadcast
    try { await matchVote(placeIdOf(p), value); } catch {}
  }, [deck, gIdx]);

  const doLeave = useCallback(async () => { try { await matchLeave(); } catch {} onExitRef.current && onExitRef.current(); }, []);
  const doClose = useCallback(async () => { try { await matchClose(); } catch {} onExitRef.current && onExitRef.current(); }, []);
  const goBack = useCallback(() => onExitRef.current && onExitRef.current(), []);

  const results = useMemo(() => {
    return Object.entries(tally)
      .filter(([, t]) => t.yes >= 1 || t.no >= 1)
      .map(([placeId, t]) => ({ placeId, ...t, place: deckById[placeId] }))
      .sort((a, b) => (b.yes - a.yes) || (a.no - b.no));
  }, [tally, deckById]);

  const participants = (snap && snap.participants) || [];
  const phase = snap && snap.phase;
  const current = deck[gIdx];

  const cardAddr = useMemo(() => {
    if (!current) return null;
    const parts = [current.address, [current.postcode, current.city].filter(Boolean).join(" ")].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }, [current]);

  const cardLinks = useMemo(() => {
    if (!current) return [];
    const out = [
      { label: "Cómo llegar", href: `https://www.google.com/maps/dir/?api=1&destination=${current.lat},${current.lon}` },
      { label: "Fotos", href: `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(current.name + " " + (current.city || "") + " restaurante")}` },
    ];
    if (current.website) out.push({ label: "Web", href: current.website });
    const ig = socialUrl("https://instagram.com/", current.instagram);
    if (ig) out.push({ label: "Instagram", href: ig });
    const fb = socialUrl("https://facebook.com/", current.facebook);
    if (fb) out.push({ label: "Facebook", href: fb });
    if (current.phone) out.push({ label: "Llamar", href: `tel:${current.phone.replace(/\s+/g, "")}` });
    return out;
  }, [current]);

  const resultsList = (
    <>
      <div className="gm-list">
        {results.map((r) => (
          <div className="gm-item" key={r.placeId}>
            <div className="gm-item-name">
              {r.place ? r.place.name : r.placeId}
              {r.place ? <span className="gm-item-sub"> · {catLabel(r.place.category)}</span> : null}
            </div>
            <div className="gm-nums">
              <span className="gm-yc">{r.yes}</span>
              <span className="gm-nc">{r.no}</span>
              <span className="gm-dc">{r.dontCare}</span>
            </div>
          </div>
        ))}
      </div>
      {results.length === 0 ? <div className="gm-empty2">Nadie ha votado sí o no.</div> : null}
    </>
  );

  return (
    <div className="gm-root">
      <style>{`
        .gm-root{position:fixed;inset:0;z-index:2500;display:flex;flex-direction:column;background:#fafafa;color:#111;
          font-family:Inter,ui-sans-serif,system-ui,-apple-system,sans-serif}
        .gm-bar{display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.85rem 1.1rem;
          border-bottom:1px solid #ececec;background:#fff}
        .gm-title{font-size:1.02rem;font-weight:600;letter-spacing:-.01em}
        .gm-sub{font-size:.72rem;color:#8a8a8a;margin-top:1px}
        .gm-back{border:none;background:transparent;color:#666;font-size:.85rem;cursor:pointer;padding:.3rem .5rem;border-radius:8px}
        .gm-back:hover{background:#f1f1f1}
        .gm-body{flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;align-items:center;padding:1.1rem}
        .gm-center{margin:auto;width:100%;max-width:440px;text-align:center}
        .gm-lead{font-size:1.15rem;font-weight:700;margin-bottom:.35rem}
        .gm-note{font-size:.86rem;color:#777;margin-bottom:1.1rem}
        .gm-btn{border-radius:14px;padding:.9rem 1rem;font-size:.95rem;font-weight:600;cursor:pointer;transition:transform .06s}
        .gm-btn:active{transform:scale(.985)}
        .gm-btn:disabled{opacity:.5;cursor:default}
        .gm-primary{border:none;background:#111;color:#fff;width:100%}
        .gm-outline{border:1px solid #d8d8d8;background:#fff;color:#333}
        .gm-danger{border:1px solid #e6c3c0;background:#fff;color:#c0362c}
        .gm-row{display:flex;gap:.6rem;width:100%;max-width:440px;margin-top:.8rem}
        .gm-row .gm-btn{flex:1}

        .gm-people{list-style:none;margin:0 0 1.2rem;padding:0;width:100%;max-width:440px}
        .gm-person{display:flex;align-items:center;gap:.6rem;padding:.55rem .3rem;border-bottom:1px solid #f0f0f0;text-align:left}
        .gm-ava{width:30px;height:30px;border-radius:999px;background:#eee;object-fit:cover;flex:none;
          display:flex;align-items:center;justify-content:center;font-size:.8rem;color:#888;font-weight:700}
        .gm-pname{font-size:.92rem}
        .gm-admin-tag{margin-left:auto;font-size:.62rem;letter-spacing:.06em;text-transform:uppercase;color:#111;
          background:#f0f0f0;border-radius:999px;padding:.2rem .5rem}

        .gm-seg{display:inline-flex;align-items:center;border:1px solid #e0e0e0;background:#f3f3f3;border-radius:999px;
          padding:3px;margin:0 auto 1rem;white-space:nowrap;flex:none}
        .gm-seg button{border:none;background:transparent;font:inherit;font-size:.8rem;font-weight:600;color:#777;
          padding:.4rem .95rem;border-radius:999px;cursor:pointer;white-space:nowrap}
        .gm-seg button.on{background:#111;color:#fff}

        .gm-card{width:100%;max-width:440px;background:#fff;border:1px solid #ececec;border-radius:20px;padding:1.3rem 1.4rem;
          box-shadow:0 12px 40px rgba(0,0,0,.08);text-align:left}
        .gm-rcard{width:100%;max-width:440px;background:#fff;border:1px solid #ececec;border-radius:20px;overflow:hidden;
          box-shadow:0 12px 40px rgba(0,0,0,.08);text-align:left}
        .gm-mini{height:140px;width:100%;background:#eef0f2}
        .gm-mini .leaflet-container{background:#eef0f2}
        .gm-cbody{padding:1.1rem 1.3rem 1.25rem}
        .gm-pin-dot{display:block;width:16px;height:16px;border-radius:999px;background:#111;border:3px solid #fff;
          box-shadow:0 0 0 2px rgba(0,0,0,.15),0 3px 8px rgba(0,0,0,.35)}
        .gm-hours{font-size:.8rem;color:#557;margin-top:.5rem;background:#f6f7fb;border-radius:8px;padding:.4rem .6rem;
          white-space:pre-wrap;word-break:break-word}
        .gm-feats{display:flex;flex-wrap:wrap;gap:.4rem;margin-top:.75rem}
        .gm-feat{font-size:.72rem;color:#3d6b3d;background:#eef7ee;border:1px solid #dcefdc;border-radius:999px;padding:.24rem .6rem}
        .gm-links{display:flex;gap:.5rem .9rem;margin-top:.9rem;flex-wrap:wrap}
        .gm-link{font-size:.82rem;color:#111;text-decoration:none;border-bottom:1px solid #ddd;padding-bottom:1px}
        .gm-link:hover{border-color:#111}
        .gm-badge{display:inline-block;font-size:.66rem;font-weight:600;letter-spacing:.03em;color:#5a5a5a;
          background:#f2f2f2;border-radius:999px;padding:.26rem .68rem;text-transform:uppercase}
        .gm-name{font-size:1.5rem;font-weight:700;letter-spacing:-.02em;line-height:1.15;margin:.6rem 0 .3rem}
        .gm-meta{font-size:.9rem;color:#666}
        .gm-meta b{color:#111}
        .gm-addr{font-size:.82rem;color:#8a8a8a;margin-top:.45rem}

        .gm-votes{display:flex;gap:.6rem;width:100%;max-width:440px;margin-top:1.1rem}
        .gm-votes .gm-btn{flex:1;font-size:1.05rem}
        .gm-no{border:none;background:#fdecea;color:#c0362c}
        .gm-skip{border:none;background:#eee;color:#555}
        .gm-yes{border:none;background:#e7f6ea;color:#1f7a3d}

        .gm-list{width:100%;max-width:440px;display:flex;flex-direction:column;gap:.5rem}
        .gm-item{display:flex;align-items:center;gap:.7rem;background:#fff;border:1px solid #ececec;border-radius:14px;padding:.7rem .85rem}
        .gm-item-name{font-size:.95rem;font-weight:600;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .gm-item-sub{font-size:.72rem;color:#9a9a9a;font-weight:400}
        .gm-nums{display:flex;gap:.5rem;flex:none;font-variant-numeric:tabular-nums;font-weight:700;font-size:.92rem}
        .gm-yc{color:#1f7a3d}.gm-nc{color:#c0362c}.gm-dc{color:#9a9a9a}
        .gm-empty2{color:#9a9a9a;font-size:.86rem;text-align:center;margin-top:1.5rem}
        .gm-notice{position:absolute;left:50%;top:14px;transform:translateX(-50%);z-index:20;background:#111;color:#fff;
          font-size:.8rem;padding:.5rem .9rem;border-radius:999px;box-shadow:0 4px 14px rgba(0,0,0,.2)}
      `}</style>

      {notice ? <div className="gm-notice" onClick={() => setNotice(null)}>{notice}</div> : null}

      <div className="gm-bar">
        <div>
          <div className="gm-title">Modo grupo</div>
          <div className="gm-sub">
            {phase === "locked" ? `Votando · ${participants.length} personas`
              : joined ? `Sala · ${participants.length} dentro`
              : "Decidir juntos"}
          </div>
        </div>
        <button className="gm-back" onClick={goBack}>‹ Volver</button>
      </div>

      <div className="gm-body">
        {snap == null ? (
          <div className="gm-center"><div className="gm-note">Cargando…</div></div>
        ) : !snap.exists ? (
          <div className="gm-center">
            <div className="gm-lead">Decidid dónde comer juntos</div>
            <div className="gm-note">Crea una sala, que se una la gente y lanza la votación.</div>
            <button className="gm-btn gm-primary" onClick={doCreate} disabled={busy}>Crear grupo</button>
            <div className="gm-row"><button className="gm-btn gm-outline" onClick={goBack}>Volver</button></div>
          </div>
        ) : !joined ? (
          phase === "lobby" ? (
            <div className="gm-center">
              <div className="gm-lead">Hay una sala abierta</div>
              <div className="gm-note">{participants.length} persona(s) dentro.</div>
              <button className="gm-btn gm-primary" onClick={doJoin} disabled={busy}>Unirse</button>
              <div className="gm-row"><button className="gm-btn gm-outline" onClick={goBack}>Volver</button></div>
            </div>
          ) : (
            <div className="gm-center">
              <div className="gm-lead">🔒 Sesión en curso</div>
              <div className="gm-note">Ya está bloqueada, no puedes unirte ahora. Espera a que termine.</div>
              <div className="gm-row"><button className="gm-btn gm-outline" onClick={goBack}>Volver</button></div>
            </div>
          )
        ) : phase === "lobby" ? (
          // ── LOBBY (joined) ──
          <>
            <ul className="gm-people">
              {participants.map((p) => (
                <li className="gm-person" key={p.userId}>
                  {p.pfpUrl
                    ? <img className="gm-ava" src={buildPfpSrc(p.pfpUrl, token)} alt="" />
                    : <span className="gm-ava">{(p.username || "?").slice(0, 1).toUpperCase()}</span>}
                  <span className="gm-pname">{p.username}</span>
                  {p.admin ? <span className="gm-admin-tag">anfitrión</span> : null}
                </li>
              ))}
            </ul>
            {snap.youAreAdmin ? (
              <>
                <button className="gm-btn gm-primary" style={{ maxWidth: 440, width: "100%" }} onClick={doBegin} disabled={busy}>
                  Empezar votación
                </button>
                <div className="gm-row">
                  <button className="gm-btn gm-outline" onClick={goBack}>Volver</button>
                  <button className="gm-btn gm-danger" onClick={doClose}>Cerrar sala</button>
                </div>
              </>
            ) : (
              <>
                <div className="gm-note">Esperando a que el anfitrión empiece…</div>
                <div className="gm-row">
                  <button className="gm-btn gm-outline" onClick={goBack}>Volver</button>
                  <button className="gm-btn gm-danger" onClick={doLeave}>Salir del grupo</button>
                </div>
              </>
            )}
          </>
        ) : phase === "finished" ? (
          // ── FINISHED (joined): results list + single exit ──
          <>
            <div className="gm-center" style={{ margin: "0 auto 1rem" }}>
              <div className="gm-lead">¡Votación terminada!</div>
              <div className="gm-note">Habéis visto todos los sitios. Estos son los votos:</div>
            </div>
            {resultsList}
            <div className="gm-row" style={{ marginTop: "1.2rem" }}>
              <button className="gm-btn gm-primary" onClick={snap.youAreAdmin ? doClose : doLeave}>Salir</button>
            </div>
          </>
        ) : (
          // ── LOCKED / VOTING (joined) ──
          <>
            <div className="gm-seg" role="tablist">
              <button className={view === "votar" ? "on" : ""} onClick={() => setView("votar")}>Votar</button>
              <button className={view === "resultados" ? "on" : ""} onClick={() => setView("resultados")}>
                Resultados{results.length ? ` (${results.length})` : ""}
              </button>
            </div>

            {view === "votar" ? (
              current ? (
                <>
                  <div className="gm-rcard">
                    <MapContainer
                      center={[current.lat, current.lon]} zoom={16} className="gm-mini"
                      zoomControl={false} attributionControl={false} dragging={false}
                      scrollWheelZoom={false} doubleClickZoom={false} keyboard={false} touchZoom={false}
                    >
                      <TileLayer url={LIGHT_TILES} />
                      <Marker position={[current.lat, current.lon]} icon={pinIcon} />
                      <MiniPan lat={current.lat} lon={current.lon} />
                    </MapContainer>
                    <div className="gm-cbody">
                      <span className="gm-badge">{catLabel(current.category)}</span>
                      <div className="gm-name">{current.name}</div>
                      <div className="gm-meta">
                        {prettyCuisine(current.cuisine) ? <><b>{prettyCuisine(current.cuisine)}</b> · </> : null}
                        a {fmtDist(current.distanceM)}
                      </div>
                      {cardAddr ? <div className="gm-addr">{cardAddr}</div> : null}
                      {current.features && current.features.length ? (
                        <div className="gm-feats">
                          {current.features.map((f) => <span className="gm-feat" key={f}>{FEATURE_LABEL[f] || f}</span>)}
                        </div>
                      ) : null}
                      {current.openingHours ? <div className="gm-hours">🕒 {current.openingHours}</div> : null}
                      <div className="gm-links">
                        {cardLinks.map((l) => (
                          <a className="gm-link" key={l.label} href={l.href} target="_blank" rel="noreferrer">{l.label} ↗</a>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="gm-votes">
                    <button className="gm-btn gm-no" onClick={() => doVote("no")}>No</button>
                    <button className="gm-btn gm-skip" onClick={() => doVote("dont_care")}>Paso</button>
                    <button className="gm-btn gm-yes" onClick={() => doVote("yes")}>Sí</button>
                  </div>
                  <div className="gm-row" style={{ marginTop: "1.1rem" }}>
                    {snap.youAreAdmin
                      ? <button className="gm-btn gm-danger" onClick={doClose}>Terminar sesión</button>
                      : <button className="gm-btn gm-danger" onClick={doLeave}>Salir del grupo</button>}
                  </div>
                </>
              ) : (
                <div className="gm-center">
                  <div className="gm-lead">Sin más sitios</div>
                  <div className="gm-note">Ya has votado todos. Mira los resultados.</div>
                  <button className="gm-btn gm-primary" onClick={() => setView("resultados")}>Ver resultados</button>
                  <div className="gm-row">
                    {snap.youAreAdmin
                      ? <button className="gm-btn gm-danger" onClick={doClose}>Terminar sesión</button>
                      : <button className="gm-btn gm-danger" onClick={doLeave}>Salir del grupo</button>}
                  </div>
                </div>
              )
            ) : (
              <>
                {resultsList}
                <div className="gm-row" style={{ marginTop: "1.2rem" }}>
                  {snap.youAreAdmin
                    ? <button className="gm-btn gm-danger" onClick={doClose}>Terminar sesión</button>
                    : <button className="gm-btn gm-danger" onClick={doLeave}>Salir del grupo</button>}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
